import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganisationProvider } from './contexts/OrganisationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { Toaster } from 'react-hot-toast';
import AppLayout from './components/Layout/AppLayout';
import LoginPage from './pages/LoginPage';
import MfaVerifyPage from './pages/MfaVerifyPage';
import MfaEnrollPage from './pages/MfaEnrollPage';
import DeviceVerifyPage from './pages/DeviceVerifyPage';
import OidcReturnPage from './pages/OidcReturnPage';
import McpSecretEntryPage from './pages/McpSecretEntryPage';
import McpConfirmationPage from './pages/McpConfirmationPage';
import McpAuthorizePage from './pages/McpAuthorizePage';
import GoogleLinkPage from './pages/GoogleLinkPage';
import TrustedDevicesPage from './pages/TrustedDevicesPage';
import RegisterPage from './pages/RegisterPage';
import VerifyAccountPage from './pages/VerifyAccountPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PlatformBanner from './components/PlatformBanner';
import DashboardPage from './pages/DashboardPage';
import DeploymentPage from './pages/DeploymentPage';
import ProjectsPage from './pages/ProjectsPage';
import EnvironmentsPage from './pages/EnvironmentsPage';
import DeploymentsPage from './pages/DeploymentsPage';
import StatusDashboard from './pages/StatusDashboard';
import DeploymentManagementPage from './pages/DeploymentManagementPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectApplicationsPage from './pages/ProjectApplicationsPage';
import BuiltImagesPage from './pages/BuiltImagesPage';
import BuildsPage from './pages/BuildsPage';
import BlueprintPage from './pages/BlueprintPage';
import NubieAIPage from './pages/NubieAIPage';
import McpServerPage from './pages/McpServerPage';
import InvitationAcceptPage from './pages/InvitationAcceptPage';
import ProfilePage from './pages/ProfilePage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import BackupManagementPage from './pages/BackupManagementPage';
import RestorePage from './pages/RestorePage';
import BackupsPage from './pages/BackupsPage';
import NubiS3Page from './pages/NubiS3Page';
import MonitoringPage from './pages/MonitoringPage';
import RepositoryConnectorV2 from './components/RepositoryConnectorV2';

// Audit Logs page
import AuditLogsPage from './pages/AuditLogsPage';

// Organisation pages
import OrganisationsPage from './pages/OrganisationsPage';
import OrganisationDashboardPage from './pages/OrganisationDashboardPage';
import OrganisationSettingsPage from './pages/OrganisationSettingsPage';
import OrganisationMembersPage from './pages/OrganisationMembersPage';
import OrganisationGroupsPage from './pages/OrganisationGroupsPage';
import OrganisationGroupDetailPage from './pages/OrganisationGroupDetailPage';
import OrganisationQuotasPage from './pages/OrganisationQuotasPage';
import OrganisationPlanPage from './pages/OrganisationPlanPage';
import OrganisationInvitationAcceptPage from './pages/OrganisationInvitationAcceptPage';

// Composant pour protéger les routes authentifiées (wraps children in AppLayout)
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = (useTranslation as any)('common');

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-0" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent-500 border-t-transparent" aria-hidden="true"></div>
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  return isAuthenticated ? <AppLayout>{children}</AppLayout> : <Navigate to="/login" />;
};

// Composant pour rediriger la racine selon l'état d'authentification
const RootRedirect: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = (useTranslation as any)('common');

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-0" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent-500 border-t-transparent" aria-hidden="true"></div>
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />;
};

// Composant pour rediriger les utilisateurs connectés
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = (useTranslation as any)('common');

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-0" role="status" aria-live="polite" aria-busy="true">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-accent-500 border-t-transparent" aria-hidden="true"></div>
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" />;
};

function AppContent() {
  const [showOAuthModal, setShowOAuthModal] = useState(false);

  // Détecter les paramètres OAuth au chargement et aéré les changements d'URL
  useEffect(() => {
    const checkOAuthParams = () => {
      const params = new URLSearchParams(window.location.search);
      const hasOAuthParams = params.has('oauth_state') || params.has('oauth_provider') ||
                            params.has('state') || params.has('provider');
      if (hasOAuthParams) {
        setShowOAuthModal(true);
      }
    };

    checkOAuthParams();

    // Écouter les changements d'URL
    const handlePopState = () => checkOAuthParams();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <div className="App">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      {/* OAuth Modal Global - Using new V2 hybrid system */}
      <RepositoryConnectorV2
        isOpen={showOAuthModal}
        onClose={() => setShowOAuthModal(false)}
        onConnected={() => {
          setShowOAuthModal(false);
        }}
      />
      <PlatformBanner />
      <Routes>
            {/* Redirection racine */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/login/mfa" element={
              <PublicRoute>
                <MfaVerifyPage />
              </PublicRoute>
            } />
            <Route path="/mfa-enroll" element={
              <PrivateRoute>
                <MfaEnrollPage />
              </PrivateRoute>
            } />
            <Route path="/login/verify-device" element={
              <PublicRoute>
                <DeviceVerifyPage />
              </PublicRoute>
            } />
            {/* Retour Google : hors PublicRoute, un utilisateur connecte y revient
                apres avoir lie Google depuis son profil (status=linked). */}
            <Route path="/login/google" element={<OidcReturnPage />} />
            <Route path="/login/google/link" element={
              <PublicRoute>
                <GoogleLinkPage />
              </PublicRoute>
            } />
            <Route path="/login/entra" element={<OidcReturnPage />} />
            <Route path="/login/entra/link" element={
              <PublicRoute>
                <GoogleLinkPage />
              </PublicRoute>
            } />
            <Route path="/account/security/devices" element={
              <PrivateRoute>
                <AppLayout><TrustedDevicesPage /></AppLayout>
              </PrivateRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/verify-account" element={
              <PublicRoute>
                <VerifyAccountPage />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } />

            {/* Route publique pour les invitations d'organisation */}
            <Route path="/accept-organisation-invitation" element={<OrganisationInvitationAcceptPage />} />

            {/* Route publique pour la vérification de compte */}
            <Route path="/verify-account" element={<VerifyAccountPage />} />

            {/* Routes Organisation (privées) */}
            {/* Saisie d'un secret demandee par un agent IA (plan MCP palier 2, C13).
                Hors de l'espace organisation : le lien est ouvert directement depuis un
                terminal, et c'est l'authentification -- pas l'adresse -- qui verifie que
                la personne est bien celle pour qui la demande a ete emise. */}
            <Route path="/mcp/saisie/:entryId" element={
              <PrivateRoute>
                <McpSecretEntryPage />
              </PrivateRoute>
            } />
            {/* Approbation d'une operation demandee par un agent IA (plan MCP palier 2,
                C17). Meme raison d'etre hors de l'espace organisation, et meme controle :
                c'est l'authentification, pas l'adresse, qui prouve le destinataire. La
                page n'execute rien -- elle enregistre une decision. */}
            <Route path="/mcp/confirmation/:requestId" element={
              <PrivateRoute>
                <McpConfirmationPage />
              </PrivateRoute>
            } />
            {/* Consentement OAuth d'un connecteur heberge (plan MCP palier 3).
                `/oauth/authorize` redirige ici apres avoir valide la demande du client.
                DEUX chemins : avec identifiant pour une demande reelle, sans pour un
                refus survenu AVANT la validation de l'adresse de retour -- ce refus ne
                peut aller nulle part ailleurs sans ouvrir une redirection, il arrive
                donc ici avec `#erreur=<code>` dans le fragment. */}
            <Route path="/mcp/autoriser" element={
              <PrivateRoute>
                <McpAuthorizePage />
              </PrivateRoute>
            } />
            <Route path="/mcp/autoriser/:identifiant" element={
              <PrivateRoute>
                <McpAuthorizePage />
              </PrivateRoute>
            } />
            <Route path="/organisations" element={
              <PrivateRoute>
                <OrganisationsPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/create" element={
              <PrivateRoute>
                <OrganisationsPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/dashboard" element={
              <PrivateRoute>
                <OrganisationDashboardPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/settings" element={
              <PrivateRoute>
                <OrganisationSettingsPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/members" element={
              <PrivateRoute>
                <OrganisationMembersPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/groups" element={
              <PrivateRoute>
                <OrganisationGroupsPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/groups/:groupId" element={
              <PrivateRoute>
                <OrganisationGroupDetailPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/quotas" element={
              <PrivateRoute>
                <OrganisationQuotasPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/plan" element={
              <PrivateRoute>
                <OrganisationPlanPage />
              </PrivateRoute>
            } />
            <Route path="/organisations/:orgId/projects" element={
              <PrivateRoute>
                <ProjectsPage />
              </PrivateRoute>
            } />

            {/* Routes privées (nécessitent authentification) */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            } />
            <Route path="/deployments" element={
              <PrivateRoute>
                <DeploymentPage />
              </PrivateRoute>
            } />
            <Route path="/deploy" element={
              <PrivateRoute>
                <DeploymentPage />
              </PrivateRoute>
            } />
            <Route path="/projects" element={
              <PrivateRoute>
                <ProjectsPage />
              </PrivateRoute>
            } />
            <Route path="/environments/:projectId" element={
              <PrivateRoute>
                <EnvironmentsPage />
              </PrivateRoute>
            } />
            <Route path="/deployments/:environmentId" element={
              <PrivateRoute>
                <DeploymentsPage />
              </PrivateRoute>
            } />
            <Route path="/status" element={
              <PrivateRoute>
                <StatusDashboard />
              </PrivateRoute>
            } />
            <Route path="/deployment/:deploymentId" element={
              <PrivateRoute>
                <DeploymentManagementPage />
              </PrivateRoute>
            } />
            <Route path="/deployments/:deploymentId/manage" element={
              <PrivateRoute>
                <DeploymentManagementPage />
              </PrivateRoute>
            } />
            <Route path="/deployments/:deploymentId/monitoring" element={
              <PrivateRoute>
                <MonitoringPage />
              </PrivateRoute>
            } />
            <Route path="/deployments/:deploymentId/backups" element={
              <PrivateRoute>
                <BackupManagementPage />
              </PrivateRoute>
            } />
            <Route path="/deployments/:deploymentId/backups/restore/:backupId" element={
              <PrivateRoute>
                <RestorePage />
              </PrivateRoute>
            } />
            <Route path="/deployments/:deploymentId/restores/:restoreId" element={
              <PrivateRoute>
                <RestorePage />
              </PrivateRoute>
            } />
            <Route path="/projects/:projectId" element={
              <PrivateRoute>
                <ProjectDetailPage />
              </PrivateRoute>
            } />
            <Route path="/project/:projectId/applications" element={
              <PrivateRoute>
                <ProjectApplicationsPage />
              </PrivateRoute>
            } />
            <Route path="/built-images" element={
              <PrivateRoute>
                <BuiltImagesPage />
              </PrivateRoute>
            } />
            <Route path="/builds" element={
              <PrivateRoute>
                <BuildsPage />
              </PrivateRoute>
            } />
            <Route path="/blueprints" element={
              <PrivateRoute>
                <BlueprintPage />
              </PrivateRoute>
            } />
            {/* NubieAI : tout le parcours jusqu'au choix du style fonctionne sans
                fournisseur de modele (docs/plan-site-vitrine-ia.md, lot 3 bis). */}
            <Route path="/nubie-ai" element={
              <PrivateRoute>
                <NubieAIPage />
              </PrivateRoute>
            } />
            {/* Le serveur MCP d'une organisation : son adresse, ses jetons, son
                activite. A ne pas confondre avec `/mcp/saisie/:id` et
                `/mcp/confirmation/:id` declares plus haut, qui sont des parcours
                ponctuels ouverts par un AGENT -- d'ou l'absence de `matchPaths` sur
                l'entree de menu, `isActive` comparant par `startsWith`. */}
            <Route path="/mcp" element={
              <PrivateRoute>
                <McpServerPage />
              </PrivateRoute>
            } />
            <Route path="/environments/:environmentId/blueprints" element={
              <PrivateRoute>
                <BlueprintPage />
              </PrivateRoute>
            } />
            <Route path="/backups" element={
              <PrivateRoute>
                <BackupsPage />
              </PrivateRoute>
            } />
            <Route path="/storage" element={
              <PrivateRoute>
                <NubiS3Page />
              </PrivateRoute>
            } />
            <Route path="/audit-logs" element={
              <PrivateRoute>
                <AuditLogsPage />
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            } />
            
            {/* Route publique pour les invitations */}
            <Route path="/invitations/:token" element={<InvitationAcceptPage />} />

            {/* OAuth Callback Handler (ouvre dans un popup) */}
            <Route path="/oauth-callback" element={<OAuthCallbackPage />} />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureFlagsProvider>
          <OrganisationProvider>
            <Router>
              <AppContent />
            </Router>
          </OrganisationProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
